/**
 * TASK-836 — a canvas of tiles has an address.
 *
 * **Canvas state had no URL representation anywhere in this product.** `canvasStore` never
 * touched `useSearchParams`, and neither did `DashboardPage` nor anything under
 * `components/canvas/`. Tiles survived a reload only if the advisor manually saved a named
 * workstream into `localStorage`.
 *
 * Everything else on the surface *is* addressable, which is what made the gap visible: the
 * Radar keeps tab, expanded row, open investigation and DNA rollup in four URL params
 * (TASK-726); modal workspaces have `?focus=workspace:entityId`; the portfolio views use the
 * same convention. The canvas — which TASK-833's DEC-1 made **the working surface for every
 * signal** — was the one that did not.
 *
 * ## Why this is small
 *
 * The serialisation round-trip already existed; it just terminated in `localStorage`.
 * `saveWorkstream` reduces live tiles to `{component, props, size}` and `openWorkstream`
 * restores them through `stampTiles`. A URL address is *a workstream in the URL*, reusing both
 * halves — and TASK-651 established the principle it rests on: *"Specs are pointers
 * (ids/filters); widgets self-fetch on restore, so no client content ever sits in browser
 * storage."*
 *
 * ## The constraint that shapes it — DEC-1
 *
 * `localStorage` is per-browser and roughly private. **A URL is not**: it lands in browser
 * history, in bookmarks, in referrer headers, in server and proxy logs, on the shoulder of
 * whoever is looking at the screen, and — the point of the feature — in a message to a
 * colleague.
 *
 * Tile `props` are **not safe to serialise wholesale**. `saveWorkstream` itself reads
 * `t.props?.clientName`, so props carry client names; in a URL that would be a client name in
 * plaintext in every log the request touches.
 *
 * So the URL form is a **narrow allow-list**: `component` plus the identity keys `tileIdentity`
 * already keys on, and nothing else. They are opaque ids, and they are sufficient because
 * widgets self-fetch. Everything else — `clientName` and any other display value — is
 * **dropped and re-fetched on restore**. A tile that cannot be reconstructed from its identity
 * keys alone is a tile whose widget is doing too much with its props, and that is a finding for
 * that widget rather than a reason to widen this.
 */

import type { WidgetSpec } from '../registry/types';

/**
 * The ONLY props that may reach a URL.
 *
 * Exactly the fields `tileIdentity` keys on (`canvasStore.ts`), plus `size`. Opaque ids, no
 * names, no free text. Adding a field here is a privacy decision, not a convenience — the
 * test `no client name can reach the param` is what enforces that.
 */
export const URL_SAFE_PROPS = [
  'clientId', 'subject', 'subjectKey', 'reviewId', 'documentId',
  'sourceEvent', 'entityKey', 'focusIsin', 'focusSac',
] as const;

export const CANVAS_PARAM = 'canvas';

/** One tile, reduced to what may be addressed. */
type UrlSpec = { c: string; p?: Record<string, string> };

export function encodeCanvas(specs: WidgetSpec[]): string | null {
  const items: UrlSpec[] = [];
  for (const spec of specs) {
    // Conversation bubbles are session-only and deliberately not persisted (TASK-651); the
    // same reasoning applies here, more so.
    if (spec.component === 'ChatMessage') continue;
    const p: Record<string, string> = {};
    for (const key of URL_SAFE_PROPS) {
      const v = spec.props?.[key];
      if (typeof v === 'string' && v) p[key] = v;
    }
    items.push(Object.keys(p).length ? { c: spec.component, p } : { c: spec.component });
  }
  // An empty canvas keeps a CLEAN url — the default is deleted, never written, so "is the
  // canvas in its default state?" stays answerable from the address (the Radar's convention).
  if (items.length === 0) return null;
  return JSON.stringify(items);
}

export interface DecodedCanvas {
  specs: WidgetSpec[];
  /** Components named in the param that no longer exist. Reported, never rendered blind. */
  unknown: string[];
  /**
   * TASK-842: the param was there and something in it could not be READ — the whole thing
   * (not JSON, not an array) or one entry inside it (not an object, no component name).
   *
   * Distinct from `unknown`, which is a link naming a widget this build removed. To the
   * advisor following the link the two are the same question — "did I get everything?" — but
   * only `unknown` has names to report, and without this flag an unparseable param was
   * indistinguishable from an absent one and reported nothing at all.
   *
   * An ABSENT param is never malformed: no param is the empty canvas, which is a normal
   * state and not a failure to restore.
   */
  malformed: boolean;
  /**
   * How many entries were discarded as unreadable. `malformed` is `dropped > 0` OR the whole
   * param being unparseable — the flag answers "was anything lost?", this answers "how much?".
   * Separate from `unknown.length` only because those have names to report and these do not;
   * the advisor is told the TOTAL, because "one tile is missing" beside two missing tiles is
   * the same class of half-true statement this ticket exists to remove.
   */
  dropped: number;
}

export function decodeCanvas(
  raw: string | null,
  isKnownComponent: (name: string) => boolean,
): DecodedCanvas {
  if (!raw) return { specs: [], unknown: [], malformed: false, dropped: 0 };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A URL is user-editable input. Malformed means "open the canvas without it", never a
    // crash and never a half-restored surface — but TASK-842: it is also never silent.
    return { specs: [], unknown: [], malformed: true, dropped: 0 };
  }
  if (!Array.isArray(parsed)) return { specs: [], unknown: [], malformed: true, dropped: 0 };

  const specs: WidgetSpec[] = [];
  const unknown: string[] = [];
  let dropped = 0;
  for (const item of parsed) {
    // TASK-842: a dropped entry counts too. It has no name to report, so it cannot go in
    // `unknown`, and before this it left no trace anywhere — the advisor saw a tile fewer
    // than the link promised and nothing said so.
    if (!item || typeof item !== 'object') { dropped += 1; continue; }
    const c = (item as UrlSpec).c;
    if (typeof c !== 'string' || !c) { dropped += 1; continue; }
    if (!isKnownComponent(c)) {
      unknown.push(c);
      continue;
    }
    const rawProps = (item as UrlSpec).p ?? {};
    const props: Record<string, unknown> = {};
    // Re-filtered on the way IN as well as out: a hand-edited param must not be able to inject
    // props the allow-list does not cover.
    for (const key of URL_SAFE_PROPS) {
      const v = rawProps[key];
      if (typeof v === 'string' && v) props[key] = v;
    }
    specs.push({ component: c, props });
  }
  return { specs, unknown, malformed: dropped > 0, dropped };
}
